import { OWNER_APP_PREFIXES, pathMatchesPrefix } from "./authRealm";

/** @typedef {'user' | 'owner' | 'admin'} SessionRealm */

export const SESSION_EXPIRED_EVENT = "lememe:session-expired";

const AUTH_BOOT_PATHS = [
  "/api/auth/user/me",
  "/api/auth/owner/me",
  "/api/superauth/superadmin/me",
  // Fired with the customer session check. For a guest it returns 401, which must
  // NOT be treated as an expired session (it would bounce guests off /cart and
  // /checkout to /login on refresh).
  "/api/users/places",
];

const LOGIN_API_PATHS = [
  "/api/user/login",
  "/api/owner/login",
  "/api/owner/employees/login",
  "/api/csrf-token",
  "/api/google/logout",
];

const APP_LOGIN_PATHS = new Set([
  "/login",
  "/signup",
  "/restaurant/login",
  "/restaurant/employee-login",
  "/admin/login",
]);

const USER_APP_PREFIXES = [
  "/cart/",
  "/checkout/",
  "/payment",
  "/orders",
  "/settings",
  "/favorites",
  "/planned/",
  "/dine-in-reservations",
  "/success",
];

const SESSION_EXPIRED_MESSAGES = new Set([
  "not authenticated",
  "invalid or expired token",
  "not authenticated as owner",
]);

let notifyCooldown = false;

function requestPath(config) {
  const url = String(config?.url || "");
  if (url.startsWith("http")) {
    try {
      return new URL(url).pathname;
    } catch {
      return url.split("?")[0];
    }
  }
  return url.split("?")[0];
}

function isAuthRelated401(status, message) {
  if (status !== 401) return false;
  const msg = String(message || "").toLowerCase();
  if (!msg) return true;
  return SESSION_EXPIRED_MESSAGES.has(msg) || msg.includes("expired") || msg.includes("authenticated");
}

/**
 * @param {string} pathname
 * @returns {SessionRealm | null}
 */
export function resolveSessionRealm(pathname) {
  const path = pathname || "";

  if (path === "/admin" || path.startsWith("/admin/set-password")) {
    return "admin";
  }

  if (OWNER_APP_PREFIXES.some((p) => pathMatchesPrefix(path, p))) {
    return "owner";
  }

  if (USER_APP_PREFIXES.some((p) => path === p || path.startsWith(p))) {
    return "user";
  }

  return null;
}

export function shouldHandleSessionExpired(config, status, message) {
  if (!isAuthRelated401(status, message)) return false;
  if (config?.skipSessionExpired) return false;

  const path = requestPath(config);
  if (AUTH_BOOT_PATHS.some((p) => path.endsWith(p))) return false;
  if (LOGIN_API_PATHS.some((p) => path.includes(p))) return false;

  if (typeof window !== "undefined") {
    const appPath = window.location.pathname;
    if (APP_LOGIN_PATHS.has(appPath)) return false;
    if (resolveSessionRealm(appPath) == null) return false;
  }

  return true;
}

/**
 * @param {{ realm: SessionRealm, from: string }} detail
 */
export function notifySessionExpired(detail) {
  if (typeof window === "undefined") return;
  if (notifyCooldown) return;

  const from = detail?.from || window.location.pathname;
  const realm = detail?.realm || resolveSessionRealm(from);
  if (!realm) return;

  notifyCooldown = true;
  window.setTimeout(() => {
    notifyCooldown = false;
  }, 3000);

  window.dispatchEvent(
    new CustomEvent(SESSION_EXPIRED_EVENT, {
      detail: { realm, from },
    })
  );
}

export function loginPathForRealm(realm) {
  if (realm === "owner") return "/restaurant/login";
  if (realm === "admin") return "/admin/login";
  return "/login";
}
